import { Image, Film, DollarSign, Zap, Clock, TrendingUp, Play, ArrowRight } from 'lucide-react'
import useStore from '../store/useStore'

export default function Dashboard() {
  const { stats, activeJobs, jobHistory, setCurrentPage } = useStore()

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">

      {/* Welcome banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-600 to-accent-purple p-6 shadow-glow-brand">
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-white mb-1">Welcome back, Barry.</h2>
          <p className="text-brand-200 text-sm">AIQ Studio is ready. Start generating.</p>
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => setCurrentPage('canvas')}
              className="bg-white text-brand-700 font-semibold text-sm px-4 py-2 rounded-lg hover:bg-brand-50 transition-colors flex items-center gap-2"
            >
              Open Canvas <ArrowRight size={15} />
            </button>
            <button
              onClick={() => setCurrentPage('storyboard')}
              className="bg-white/20 text-white font-semibold text-sm px-4 py-2 rounded-lg hover:bg-white/30 transition-colors"
            >
              New Video
            </button>
          </div>
        </div>
        {/* Background decoration */}
        <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full bg-white/5" />
        <div className="absolute -right-4 top-8 w-32 h-32 rounded-full bg-white/5" />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard icon={Image} label="Images Generated" value={stats.totalImages} color="blue" />
        <StatCard icon={Film} label="Video Clips" value={stats.totalClips} color="purple" />
        <StatCard icon={Zap} label="Videos Assembled" value={stats.totalVideos} color="cyan" />
        <StatCard icon={DollarSign} label="Total Spend" value={`$${stats.totalSpend.toFixed(2)}`} color="green" sub="vs ~$450/mo before" />
      </div>

      {/* Active jobs + Quick actions */}
      <div className="grid grid-cols-2 gap-4">

        {/* Active Jobs */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Clock size={15} className="text-brand-400" />
            Active Jobs
            {activeJobs.length > 0 && (
              <span className="badge-running ml-auto">{activeJobs.length} running</span>
            )}
          </h3>
          {activeJobs.length === 0 ? (
            <div className="py-6 text-center text-slate-500 text-sm">
              No active jobs. Start generating!
            </div>
          ) : (
            <div className="space-y-2">
              {activeJobs.map(job => (
                <JobRow key={job.id} job={job} />
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <TrendingUp size={15} className="text-brand-400" />
            Quick Actions
          </h3>
          <div className="space-y-2">
            <QuickAction
              label="Generate Static Images"
              sub="FLUX.1 Dev + Character LoRA"
              cost="~$0.002/image"
              onClick={() => setCurrentPage('quick-image')}
            />
            <QuickAction
              label="Create Video Clip"
              sub="LTX-Video 2, up to 10 seconds"
              cost="~$0.011/clip"
              onClick={() => setCurrentPage('quick-video')}
            />
            <QuickAction
              label="Build Full Video"
              sub="Storyboard → Assembled Video"
              cost="~$0.48 / 5 min"
              onClick={() => setCurrentPage('storyboard')}
            />
            <QuickAction
              label="Manage Characters"
              sub="LoRA profiles and training"
              cost="~$0.50 to train"
              onClick={() => setCurrentPage('character-manager')}
            />
          </div>
        </div>
      </div>

      {/* Recent history */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Play size={15} className="text-brand-400" />
          Recent Generations
        </h3>
        {jobHistory.length === 0 ? (
          <div className="py-8 text-center text-slate-500 text-sm">
            Your generation history will appear here.
          </div>
        ) : (
          <div className="space-y-2">
            {jobHistory.slice(0, 5).map(job => (
              <HistoryRow key={job.id} job={job} />
            ))}
          </div>
        )}
      </div>

    </div>
  )
}

function StatCard({ icon: Icon, label, value, color, sub }) {
  const colors = {
    blue:   { bg: 'bg-blue-500/10',   border: 'border-blue-500/20',   icon: 'text-blue-400' },
    purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/20', icon: 'text-purple-400' },
    cyan:   { bg: 'bg-cyan-500/10',   border: 'border-cyan-500/20',   icon: 'text-cyan-400' },
    green:  { bg: 'bg-green-500/10',  border: 'border-green-500/20',  icon: 'text-green-400' },
  }
  const c = colors[color] || colors.blue

  return (
    <div className={`card p-4 border ${c.border}`}>
      <div className={`w-9 h-9 rounded-lg ${c.bg} flex items-center justify-center mb-3`}>
        <Icon size={18} className={c.icon} />
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-xs text-slate-400 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-green-400 mt-1">{sub}</div>}
    </div>
  )
}

function JobRow({ job }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-surface border border-surface-border">
      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-200 truncate">{job.label || job.type || 'Generating...'}</div>
        <div className="text-xs text-slate-500">{job.status || 'IN_QUEUE'}</div>
      </div>
      <span className="badge-running">Running</span>
    </div>
  )
}

function HistoryRow({ job }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-hover transition-colors">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
        job.status === 'complete' ? 'bg-green-400' : 'bg-red-400'
      }`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-200 truncate">{job.label || job.type}</div>
        <div className="text-xs text-slate-500">
          {job.completedAt ? new Date(job.completedAt).toLocaleString() : '—'}
        </div>
      </div>
      <span className={job.status === 'complete' ? 'badge-complete' : 'badge-error'}>
        {job.status === 'complete' ? 'Done' : 'Failed'}
      </span>
    </div>
  )
}

function QuickAction({ label, sub, cost, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-surface-hover border border-surface-border transition-all duration-150 text-left group"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-200 font-medium group-hover:text-white">{label}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </div>
      <div className="text-xs text-green-400 font-medium flex-shrink-0">{cost}</div>
      <ArrowRight size={14} className="text-slate-500 group-hover:text-brand-400 flex-shrink-0 transition-colors" />
    </button>
  )
}
